import React, { useState } from 'react';
import { cn } from '../../utils/cn';

/**
 * Avatar Component
 *
 * Displays user profile images with fallback to initials.
 * Uses standard Tailwind classes for maximum compatibility.
 */

const sizes = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
  '2xl': 'w-20 h-20 text-xl',
  '3xl': 'w-24 h-24 text-2xl',
};

const statusSizes = {
  xs: 'w-1.5 h-1.5 border',
  sm: 'w-2 h-2 border',
  md: 'w-2.5 h-2.5 border-2',
  lg: 'w-3 h-3 border-2',
  xl: 'w-3.5 h-3.5 border-2',
  '2xl': 'w-4 h-4 border-2',
  '3xl': 'w-5 h-5 border-2',
};

const statusColors = {
  online: 'bg-green-500',
  offline: 'bg-gray-400',
  busy: 'bg-red-500',
  away: 'bg-amber-500',
};

// Generate initials from name
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// Generate consistent color from string
function getColorFromString(str) {
  if (!str) return '#9ca3af'; // gray-400

  const colors = [
    '#3b82f6', // blue-500
    '#f59e0b', // amber-500
    '#10b981', // emerald-500
    '#8b5cf6', // violet-500
    '#ec4899', // pink-500
    '#06b6d4', // cyan-500
    '#f97316', // orange-500
    '#14b8a6', // teal-500
  ];

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

export function Avatar({
  src,
  alt,
  name,
  size = 'md',
  status,
  square = false,
  bordered = false,
  className,
  ...props
}) {
  const [imageError, setImageError] = useState(false);
  const showFallback = !src || imageError;

  return (
    <div
      className={cn(
        'relative inline-flex items-center justify-center flex-shrink-0',
        'font-semibold',
        'overflow-hidden',
        sizes[size],
        square ? 'rounded-lg' : 'rounded-full',
        bordered && 'ring-2 ring-white ring-offset-2',
        className
      )}
      {...props}
    >
      {showFallback ? (
        // Fallback with initials
        <div
          className="w-full h-full flex items-center justify-center text-white"
          style={{ backgroundColor: getColorFromString(name) }}
        >
          {getInitials(name)}
        </div>
      ) : (
        // Image
        <img
          src={src}
          alt={alt || name || 'Avatar'}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      )}

      {/* Status indicator */}
      {status && (
        <span
          className={cn(
            'absolute bottom-0 right-0',
            'rounded-full',
            'border-white',
            statusSizes[size],
            statusColors[status]
          )}
        />
      )}
    </div>
  );
}

/**
 * Avatar with name and optional subtitle
 */
export function AvatarWithName({
  src,
  name,
  subtitle,
  size = 'md',
  status,
  reverse = false,
  className,
  ...props
}) {
  const textSizes = {
    xs: { name: 'text-xs', subtitle: 'text-[10px]' },
    sm: { name: 'text-sm', subtitle: 'text-xs' },
    md: { name: 'text-sm', subtitle: 'text-xs' },
    lg: { name: 'text-base', subtitle: 'text-sm' },
    xl: { name: 'text-lg', subtitle: 'text-sm' },
    '2xl': { name: 'text-xl', subtitle: 'text-base' },
    '3xl': { name: 'text-2xl', subtitle: 'text-lg' },
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3',
        reverse && 'flex-row-reverse',
        className
      )}
      {...props}
    >
      <Avatar src={src} name={name} size={size} status={status} />
      <div className={cn('flex flex-col', reverse && 'items-end')}>
        <span className={cn('font-semibold text-gray-900', textSizes[size].name)}>
          {name}
        </span>
        {subtitle && (
          <span className={cn('text-gray-500', textSizes[size].subtitle)}>
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Avatar Group - Overlapping avatars
 */
export function AvatarGroup({
  avatars,
  size = 'md',
  max = 4,
  className,
  ...props
}) {
  const displayAvatars = avatars.slice(0, max);
  const remaining = avatars.length - max;

  const overlapSizes = {
    xs: '-space-x-2',
    sm: '-space-x-2',
    md: '-space-x-3',
    lg: '-space-x-4',
    xl: '-space-x-5',
    '2xl': '-space-x-6',
    '3xl': '-space-x-8',
  };

  return (
    <div
      className={cn('flex items-center', overlapSizes[size], className)}
      {...props}
    >
      {displayAvatars.map((avatar, index) => (
        <Avatar
          key={avatar.id || index}
          src={avatar.src}
          name={avatar.name}
          size={size}
          className="ring-2 ring-white hover:z-10 hover:scale-110 transition-transform duration-150"
          style={{ zIndex: displayAvatars.length - index }}
        />
      ))}
      {remaining > 0 && (
        <div
          className={cn(
            'relative inline-flex items-center justify-center flex-shrink-0',
            'font-semibold',
            'bg-gray-200',
            'text-gray-600',
            'rounded-full',
            'ring-2 ring-white',
            sizes[size]
          )}
        >
          +{remaining}
        </div>
      )}
    </div>
  );
}

export default Avatar;
